<template>
  <div class="section">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/admin">Admin Settings</router-link></li>
        <li class="is-active">
          <router-link to="/dashboard/user-management" aria-current="true">User Management</router-link>
        </li>
      </ul>
    </nav>
    <div class="container">
      <h1 class="title">User Management</h1>
      <div class="field has-addons">
        <div class="control">
          <input
            v-model="searchQuery"
            class="input is-small"
            type="text"
            placeholder="Search by username or email"
          />
        </div>
        <div class="control">
          <button class="button is-primary is-small" @click="searchUsers">Search</button>
        </div>
      </div>
      <div class="buttons">
        <button class="button is-primary is-small mt-5" @click="openCreateModal">
          Create User
        </button>
        <button class="button is-info is-small mt-5" @click="downloadAdminReport">
          Admin Report
        </button>
      </div>
      <table class="table is-fullwidth is-small mt-5">
        <thead>
          <tr>
            <th class="is-size-7" @click="sortBy('username')">
              Username
              <span v-if="sortColumn === 'username'">
                <span v-if="sortOrder === 'asc'">&#9650;</span>
                <span v-else>&#9660;</span>
              </span>
            </th>
            <th class="is-size-7" @click="sortBy('first_name')">
              First Name
              <span v-if="sortColumn === 'first_name'">
                <span v-if="sortOrder === 'asc'">&#9650;</span>
                <span v-else>&#9660;</span>
              </span>
            </th>
            <th class="is-size-7" @click="sortBy('last_name')">
              Last Name
              <span v-if="sortColumn === 'last_name'">
                <span v-if="sortOrder === 'asc'">&#9650;</span>
                <span v-else>&#9660;</span>
              </span>
            </th>
            <th class="is-size-7" @click="sortBy('email')">
              Email
              <span v-if="sortColumn === 'email'">
                <span v-if="sortOrder === 'asc'">&#9650;</span>
                <span v-else>&#9660;</span>
              </span>
            </th>
            <th class="is-size-7" @click="sortBy('is_active')">
              Status
              <span v-if="sortColumn === 'is_active'">
                <span v-if="sortOrder === 'asc'">&#9650;</span>
                <span v-else>&#9660;</span>
              </span>
            </th>
            <th class="is-size-7" @click="sortBy('is_staff')">
              Admin
              <span v-if="sortColumn === 'is_staff'">
                <span v-if="sortOrder === 'asc'">&#9650;</span>
                <span v-else>&#9660;</span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in sortedUsers"
            :key="user.id"
            class="is-vcentered"
            @click="openEditModal(user.id)"
            style="cursor: pointer;"
          >
            <td class="is-size-7">{{ user.username }}</td>
            <td class="is-size-7">{{ user.email }}</td>
            <td class="is-size-7">{{ user.first_name }}</td>
            <td class="is-size-7">{{ user.last_name }}</td>
            <td class="is-size-7">
              <span v-if="user.is_active" class="icon has-text-success">Active</span>
              <span v-else class="icon has-text-danger">Locked</span>
            </td>
            <td class="is-size-7">
              <span v-if="user.is_staff && !user.is_superuser">Admin</span>
              <span v-if="user.is_superuser">Super-Admin</span>
            </td>
          </tr>
        </tbody>
      </table>
      <button v-if="nextPage" class="button is-primary is-small" @click="fetchUsers(nextPage)">
        Load More
      </button>
    </div>

    <!-- User Modal with Slide Transition -->
    <transition name="slide">
      <UserModal
        v-if="isModalActive"
        :isActive="isModalActive"
        :isEdit="isEditMode"
        :user="editUser"
        :allGroups="allGroups"
        :allSenderEntities="allSenderEntities"
        :allStoreCodes="allStoreCodes"
        @close="closeModal"
        @save="handleSave"
        @delete="confirmDelete"
        @reset-password="resetPassword"
      />
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import UserModal from "./UserModal.vue"; // Adjust the path as necessary
import { toast } from "bulma-toast";

export default {
  components: {
    UserModal,
  },
  data() {
    return {
      users: [],
      nextPage: null,
      searchQuery: "",
      isModalActive: false,
      isEditMode: false,
      allGroups: [],
      allSenderEntities: [],
      allStoreCodes: [],
      editUser: this.getEmptyUser(),
      sortColumn: "", // default sort column
      sortOrder: "asc",      // default sort order
    };
  },
  computed: {
    sortedUsers() {
      if (!this.sortColumn) {
        return this.users;
      }
      return this.users.slice().sort((a, b) => {
        const modifier = this.sortOrder === "asc" ? 1 : -1;
        if (a[this.sortColumn] < b[this.sortColumn]) return -1 * modifier;
        if (a[this.sortColumn] > b[this.sortColumn]) return 1 * modifier;
        return 0;
      });
    },
  },
  methods: {
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortOrder = "asc";
      }
    },
    getEmptyUser() {
      return {
        id: "",
        username: "",
        email: "",
        first_name: "",
        last_name: "",
        is_active: "",
        is_staff: "",
        is_superuser: "",
        access_type: "",
        sender_entities: [],
        store_code: [],
        groups: [],
        password: "",
      };
    },
    async createUser(user) {
      try {
        await axios.put("/api/v1/user-profile/update/", user, {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        // Refresh the entire user list after creation
        await this.fetchUsers("/api/v1/users-list/", false);
        this.closeModal();
        toast({
          message: "User created successfully",
          type: "is-success",
          dismissible: true,
          duration: 3000,
          position: "top-right",
        });
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
        this.showErrorToast(error);
      }
    },
    async updateUser(user) {
      try {
        await axios.put(`/api/v1/user-profile/update/${user.id}/`, user, {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        // Refresh the entire user list after update
        await this.fetchUsers("/api/v1/users-list/", false);
        this.closeModal();
        toast({
          message: "User updated successfully",
          type: "is-success",
          dismissible: true,
          duration: 3000,
          position: "top-right",
        });
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
        this.showErrorToast(error);
      }
    },
    async downloadAdminReport() {
      try {
        const response = await axios.get("/api/v1/user-profile-report/download_user_report", {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "user_report.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading report:", error);
        this.showErrorToast(error);
      }
    },
    openCreateModal() {
      this.isEditMode = false;
      this.editUser = this.getEmptyUser();
      this.isModalActive = true;
    },
    async openEditModal(userId) {
      try {
        const response = await axios.get(`/api/v1/user-profile/${userId}/`, {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        this.editUser = {
          ...response.data,
          sender_entities: response.data.sender_entities.map((e) => e.id),
          store_code: response.data.store_code.map((s) => s.id),
          groups: response.data.groups.map((g) => g.id),
          id: userId,
        };
        this.isEditMode = true;
        this.isModalActive = true;
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
        this.showErrorToast(error);
      }
    },
    closeModal() {
      this.isModalActive = false;
    },
    async handleSave(user) {
      if (!user.username || !user.email || !user.first_name) {
        toast({
          message: "Please fill in all required fields.",
          type: "is-danger",
          dismissible: true,
          duration: 5000,
          position: "top-right",
        });
        return;
      }
      if (
        user.sender_entities.length === 0 &&
        user.store_code.length === 0 &&
        user.groups.length === 0
      ) {
        toast({
          message: "Please select at least one sender entity, store code, or role.",
          type: "is-danger",
          dismissible: true,
          duration: 5000,
          position: "top-right",
        });
        return;
      }
      if (this.isEditMode) {
        await this.updateUser(user);
      } else {
        await this.createUser(user);
      }
    },
    async confirmDelete(userId) {
      if (confirm("Are you sure you want to delete this user?")) {
        this.deleteUser(userId);
      }
    },
    async deleteUser(userId) {
      try {
        await axios.delete(`/api/v1/users/${userId}/`, {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        // Refresh the entire user list after deletion
        await this.fetchUsers("/api/v1/users-list/", false);
        this.closeModal();
        toast({
          message: "User deleted successfully",
          type: "is-success",
          dismissible: true,
          duration: 3000,
          position: "top-right",
        });
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
        this.showErrorToast(error);
      }
    },
    async resetPassword(userId) {
      const newPassword = prompt("Enter new password:");
      if (newPassword) {
        try {
          await axios.post(
            "/api/v1/users/set_password/",
            { id: userId, new_password: newPassword },
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("token")}`,
              },
            }
          );
          alert("Password reset successfully");
        } catch (error) {
          console.error("There was a problem with the fetch operation:", error);
          this.showErrorToast(error);
        }
      }
    },
    async fetchUsers(url = null, append = true) {
      const apiUrl = url || "/api/v1/users-list/";
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        if (append) {
          this.users = this.users.concat(response.data.results || response.data);
        } else {
          this.users = response.data.results || response.data;
        }
        this.nextPage = response.data.next;
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
        this.showErrorToast(error);
      }
    },
    async fetchAdditionalData() {
      try {
        const senderEntitiesResponse = await axios.get("/api/v1/all-senders", {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        this.allSenderEntities = senderEntitiesResponse.data;
        const storeCodesResponse = await axios.get("/api/v1/all-store-codes/", {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        this.allStoreCodes = storeCodesResponse.data;
        const groupsResponse = await axios.get("/api/v1/groups/", {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        this.allGroups = groupsResponse.data;
      } catch (error) {
        console.error("There was a problem with fetching additional data:", error);
        this.showErrorToast(error);
      }
    },
    async searchUsers() {
      await this.fetchUsers(`/api/v1/users-list/?search=${this.searchQuery}`, false);
    },
    showErrorToast(error) {
      const errorMessage =
        error.response?.data?.detail ||
        "An error occurred. Please try again.";
      toast({
        message: errorMessage,
        type: "is-danger",
        dismissible: true,
        duration: 5000,
        position: "top-right",
      });
    },
  },
  async created() {
    await this.fetchUsers();
    await this.fetchAdditionalData();
  },
};
</script>

<style scoped>
/* Row hover highlight */
table tbody tr:hover {
  background-color: #f5f5f5;
}

/* Slide transition for the modal */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter {
  transform: translateX(100%);
}
.slide-leave-to {
  transform: translateX(100%);
}
</style>
